<template>
    <main id="top" class="landing">
        <div id="landing-deploy-marker" class="d-none" data-marker="landing-deploy-check-2026-02-20" aria-hidden="true"></div>
        <LandingNav
            :freeze-scrolled="dialogsOpen"
            :theme="theme"
            @toggle-theme="toggleTheme"
            @contact="openContact"
            @early-access="openEarlyAccess" />
        <LandingHero :screenshots="landingScreenshots" :theme="theme" />
        <LandingFeatureShowcases :features="showcaseFeatures" :more-items="moreItems" :more-boxes="moreHighlights" />
        <LandingPricing :features="pricingFeatures" />
        <LandingFaq :faqs="faqs" @contact="openContact" />
        <LandingRoadmap :theme="theme" @not-for-you="openNotForYou" @contact="openContact" />
        <LandingEarlyAdopter />
        <LandingFooter :theme="theme" @dialog-state="legalDialogsOpen = $event" />

        <LandingContactDialog v-model="contactOpen" />
        <LandingWaitlistDialog v-model="waitlistDialogOpen" />
        <LandingNotForYouDialog v-model="notForYouOpen" />
    </main>
</template>

<script lang="ts" setup>
    import { useLandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import { landingScreenshots } from '@/pages/Landing/landingScreenshots';
    import { scrollToHash } from '@/utils/scrollToHash';
    import { computed, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import LandingContactDialog from './components/LandingContactDialog.vue';
    import LandingEarlyAdopter from './components/LandingEarlyAdopter.vue';
    import LandingFaq from './components/LandingFaq.vue';
    import LandingFeatureShowcases from './components/LandingFeatureShowcases.vue';
    import LandingFooter from './components/LandingFooter.vue';
    import LandingHero from './components/LandingHero.vue';
    import LandingNav from './components/LandingNav.vue';
    import LandingNotForYouDialog from './components/LandingNotForYouDialog.vue';
    import LandingPricing from './components/LandingPricing.vue';
    import LandingRoadmap from './components/LandingRoadmap.vue';
    import LandingWaitlistDialog from './components/LandingWaitlistDialog.vue';

    const contactOpen = ref(false);
    const waitlistDialogOpen = ref(false);
    const notForYouOpen = ref(false);
    const legalDialogsOpen = ref(false);
    const dialogsOpen = computed(() => contactOpen.value || waitlistDialogOpen.value || notForYouOpen.value || legalDialogsOpen.value);

    const route = useRoute();

    const { theme, toggleTheme } = useLandingTheme();

    const openContact = () => {
        contactOpen.value = true;
    };

    const openNotForYou = () => {
        notForYouOpen.value = true;
    };

    const openEarlyAccess = () => {
        waitlistDialogOpen.value = true;
    };

    const normalizeHash = (hash: string | null | undefined) => {
        if (!hash || hash === '#') {
            return null;
        }

        const raw = hash.startsWith('#') ? hash.slice(1) : hash;
        if (!raw) {
            return null;
        }

        try {
            return decodeURIComponent(raw).toLowerCase();
        } catch {
            return raw.toLowerCase();
        }
    };

    const handleHashNavigation = async (hash: string | null | undefined) => {
        const normalized = normalizeHash(hash);

        if (normalized === 'contact-us') {
            openContact();
            return;
        }

        if (normalized === 'sign-up') {
            await scrollToHash('#early-adopter');
            return;
        }

        await scrollToHash(hash);
    };

    onMounted(() => {
        void handleHashNavigation(route.hash);
    });

    watch(
        () => route.hash,
        (hash) => {
            void handleHashNavigation(hash);
        },
    );

    const showcaseFeatures = [
        {
            title: 'Give your customers a place to order',
            accent: '#7c3aed',
            bullets: [
                'Customize the content & the style of your ordering page',
                'Set availability constraints',
                'Customer info is captured for future orders',
                'Receive notifications when your customers place an order',
                'View placed orders in your dashboard, instantly see if you have enough product on hand, or supplies to create them',
            ],
        },
        {
            title: 'Get a handle on your inventory',
            accent: '#06b6d4',
            bullets: [
                'See instantly if you have enough supplies for upcoming orders',
                'Get alerts when you are running low',
                'Keep track of supply & product movements',
                'Know what you need to restock before creating batches',
            ],
        },
        {
            title: 'Generate invoices from your orders',
            accent: '#f97316',
            bullets: [
                'Create an invoice from any order, or multiple orders',
                'Send your invoices directly to your customers',
                'Modify invoices to add adjustments and custom items',
            ],
        },
        {
            title: 'Get a deep view of your finances',
            accent: '#22c55e',
            bullets: [
                'See exact revenue, profit, and expenses',
                'Analytics are driven by your products and orders',
                'Know your margins for your recipes & products',
                'Keep track of supply usage, and spoilage',
            ],
        },
    ];

    const moreItems = [
        {
            icon: 'mdi-account-group-outline',
            title: 'Manage customers.',
            description: 'Customers can be created manually, during order creation, or when they create an order on your form.',
        },
        {
            icon: 'mdi-calendar-month-outline',
            title: 'Organize your calendar.',
            description: 'Orders automatically show on your calendar, and can be filtered by their status. You can also add custom events!',
        },
        {
            icon: 'mdi-package-variant-closed',
            title: 'Get hands on with your products.',
            description:
                'Products are connected to recipes and orders. You can also assign packaging requirements. You can track your product inventory, and it automatically adjusts as you fulfill orders.',
        },
        {
            icon: 'mdi-cash-multiple',
            title: 'Enter your expenses and purchases.',
            description:
                'Add one-off, or recurring business expenses and record inventory purchases. This keeps your inventory up to date and your analytics helpful.',
        },
        {
            icon: 'mdi-book-open-page-variant',
            title: 'Create your recipes.',
            description:
                'Add or import recipes from websites. Use the recipe walkthrough to be guided through the recipe interactively. Instantly see how much each recipe is costing you. Attach and print documents such as labels and allergen sheets.',
        },
    ];

    const moreHighlights = [
        {
            title: 'Scheduled Orders',
            subtitle: "Configure recurring orders to know what's coming up and when.",
            accent: '#f97316',
            imageSrc: '/images/landing-page/v3/screens/slides/scheduled-orders_thumbnail.jpeg',
            fullImageSrc: '/images/landing-page/v3/screens/slides/scheduled-orders.jpeg',
            imageAlt: 'Scheduled Orders screenshot',
        },
        {
            title: 'Dark Mode!',
            subtitle: 'For those late night bakes!',
            accent: '#7257d1',
            imageSrc: '/images/landing-page/v3/screens/slides/dark-mode_thumbnail.jpeg',
            fullImageSrc: '/images/landing-page/v3/screens/slides/dark-mode.jpeg',
            imageAlt: 'Dark mode screenshot',
        },
        {
            title: 'Sales & POS',
            subtitle: 'Enter your sales as they happen, on your porch, at a farmers market, or at the register!',
            accent: '#06b6d4',
            imageSrc: '/images/landing-page/v3/screens/slides/sales-sessions_thumbnail.jpeg',
            fullImageSrc: '/images/landing-page/v3/screens/slides/sales-sessions.jpeg',
            imageAlt: 'Sales and POS screenshot',
        },
    ];

    const pricingFeatures = [
        'Orders + scheduled orders',
        'Calendar planning',
        'Recipes with components and costing',
        'Products and production batches',
        'Supplies and inventory ledger',
        'Purchases and vendors',
        'Invoices and payments',
        'POS sessions, expenses, and analytics',
        'Settings and notifications',
    ];

    const faqs = [
        { q: 'Do I have to track inventory?', a: 'No. Track as much as you want; services and non-tracked items are supported.' },
        { q: 'Can I reuse sub-recipes?', a: 'Yes, component recipes roll up costs and ingredients automatically.' },
        {
            q: 'Why was this created?',
            a: "My wife started a cottage bakery and quickly ran into the messy parts nobody warns you about. Inventory was hard to track, costs were hard to pin down, orders were scattered, and profit was mostly guesswork. We tried piecing together tools, spreadsheets, and apps, but each one solved a small part and made the overall process harder.\n\nSo we built Mix n' Batch to make the day-to-day simpler. It puts orders, inventory, and costing in one place, and it's designed to stay out of your way while you work.",
        },
        {
            q: 'What kind of support do you offer?',
            a: "We offer fast, friendly, and direct support. Whether you have questions, run into issues, or just need guidance, we're here to help.",
        },
    ];
</script>

<style lang="scss">
    .landing {
        font-family:
            'Nunito',
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            sans-serif;
        background: var(--l2-bg);
        color: var(--l2-text);
        --landing-accent: var(--l2-accent);
        --landing-text: var(--l2-text);
        --landing-muted: var(--l2-text-muted);
        --landing-border: var(--l2-border-subtle);
        --landing-bg: var(--l2-bg-elevated);
        --l2-section-padding-y: 100px;
        min-height: 100vh;
        overflow-x: hidden;
        scroll-margin-top: 120px;

        @media (max-width: 600px) {
            --l2-section-padding-y: 56px;
        }

        @supports (content-visibility: auto) {
            > section:not(.l2-hero),
            > footer {
                content-visibility: auto;
                contain-intrinsic-size: 1px 900px;
            }
        }

        section[id] {
            scroll-margin-top: 120px;
        }

        // Background grain texture
        &::before {
            content: '';
            position: fixed;
            inset: 0;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
            opacity: var(--l2-grain-opacity, 0.03);
            pointer-events: none;
            z-index: 0;
        }

        .v-icon {
            color: var(--l2-accent);
        }

        .l2-btn-primary {
            background: linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%) !important;
            color: #1a1025 !important;
            box-shadow: 0 6px 24px rgba(167, 139, 250, 0.2);
            transition: all var(--l2-transition);

            .v-icon {
                color: #1a1025 !important;
            }

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 32px rgba(167, 139, 250, 0.3);
            }
        }

        .l2-btn-ghost {
            border-color: var(--l2-border-subtle) !important;
            color: var(--l2-text) !important;
            transition: all var(--l2-transition);

            &:hover {
                background: var(--l2-accent-soft) !important;
                border-color: var(--l2-border) !important;
            }
        }
    }

    html.has-landing[data-landing-theme='light'] .landing {
        --landing-accent: #7257d1;
        --landing-text: #0f172a;
        --landing-muted: #475569;
        --landing-border: #e2e8f0;
        --landing-bg: #ffffff;
    }

    // Animation keyframes
    @keyframes l2-fade-up {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes l2-fade-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes l2-glow-pulse {
        0%,
        100% {
            opacity: 0.4;
        }
        50% {
            opacity: 0.7;
        }
    }

    @keyframes l2-float {
        0%,
        100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-10px);
        }
    }

    .l2-animate-fade-up {
        animation: l2-fade-up 0.8s ease-out forwards;
        opacity: 0;
    }

    .l2-animate-fade-in {
        animation: l2-fade-in 0.6s ease-out forwards;
        opacity: 0;
    }
</style>
